import { Controller } from 'stimulus'

// Every occurrence is highlighted in place instead of being jumped to one at a time,
// and the shortcuts panel reports how many sit in each section.
const MIN_QUERY    = 2
const DEBOUNCE_MS  = 120
const SKIPPED_TAGS = ['SCRIPT', 'STYLE', 'TEXTAREA', 'SELECT', 'OPTION', 'KBD']

export default class extends Controller {
  static targets = ['input', 'controls', 'count', 'summary', 'shortcut', 'tocLink', 'tocRow']

  connect() {
    this.hits    = []
    this.current = -1
    this.heading = this.hasSummaryTarget ? this.summaryTarget.textContent : ''

    if (this.hasShortcutTarget) this.shortcutTarget.textContent = this.shortcutLabel()

    this.onKeydown = event => this.interceptFind(event)
    document.addEventListener('keydown', this.onKeydown)

    // Switching to edit mode reveals hidden rows, so the hit list has to be rebuilt.
    this.observer = new MutationObserver(() => { if (this.query()) this.run() })
    this.observer.observe(this.element, { attributes: true, attributeFilter: ['class'] })
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown)
    this.observer?.disconnect()
    if (this.debounce) clearTimeout(this.debounce)
    this.removeHighlights()
  }

  // Cmd-F lands here instead of the browser's own find bar.
  interceptFind(event) {
    if (event.key !== 'f' && event.key !== 'F') return
    if (!event.metaKey && !event.ctrlKey) return
    if (event.altKey) return

    event.preventDefault()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  keydown(event) {
    if (event.key === 'Escape') return this.clear()
    if (event.key !== 'Enter')  return

    event.preventDefault()
    event.shiftKey ? this.previous() : this.next()
  }

  search() {
    if (this.debounce) clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.run(), DEBOUNCE_MS)
  }

  clear() {
    this.inputTarget.value = ''
    this.run()
    this.inputTarget.focus()
  }

  next()     { this.move(1) }
  previous() { this.move(-1) }

  query() {
    return this.normalize(this.inputTarget.value.trim())
  }

  run() {
    this.removeHighlights()

    const query = this.query()
    if (query.length < MIN_QUERY) return this.reset()

    this.highlight(query)
    this.current = this.hits.length ? 0 : -1
    this.render()
    this.reveal()
  }

  highlight(query) {
    this.collectTextNodes(this.element).forEach(node => {
      const haystack = this.normalize(node.nodeValue)
      let   index    = haystack.indexOf(query)
      if (index === -1) return

      const fragment = document.createDocumentFragment()
      const text     = node.nodeValue
      let   cursor   = 0

      while (index !== -1) {
        if (index > cursor) fragment.appendChild(document.createTextNode(text.slice(cursor, index)))

        const mark = document.createElement('mark')
        mark.className   = 'ap-hit'
        mark.textContent = text.slice(index, index + query.length)
        fragment.appendChild(mark)
        this.hits.push(mark)

        cursor = index + query.length
        index  = haystack.indexOf(query, cursor)
      }

      if (cursor < text.length) fragment.appendChild(document.createTextNode(text.slice(cursor)))
      node.parentNode.replaceChild(fragment, node)
    })
  }

  removeHighlights() {
    this.element.querySelectorAll('mark.ap-hit').forEach(mark => {
      const parent = mark.parentNode
      parent.replaceChild(document.createTextNode(mark.textContent), mark)
      parent.normalize()
    })

    this.hits    = []
    this.current = -1
  }

  collectTextNodes(root) {
    const nodes = []

    const walk = element => {
      element.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          if (child.nodeValue.trim()) nodes.push(child)
        } else if (child.nodeType === Node.ELEMENT_NODE && !this.skipped(child)) {
          walk(child)
        }
      })
    }

    walk(root)
    return nodes
  }

  // Counting hits the user cannot see promises occurrences they will never find on
  // screen: rows hidden in read mode, Cocoon row templates, React mount points.
  skipped(element) {
    if (SKIPPED_TAGS.includes(element.tagName))            return true
    if (element.hasAttribute('data-react-class'))          return true
    if (element.hidden)                                    return true
    if (element.classList.contains('d-none'))              return true
    if (element.classList.contains('ap-search'))           return true
    if (element.classList.contains('acc-preferences-toc')) return true

    return getComputedStyle(element).display === 'none'
  }

  // Folded one character at a time so the result keeps the length of the source and
  // the match indexes still point at the right characters.
  normalize(text) {
    let folded = ''

    for (const char of text) {
      const stripped = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      folded += stripped.length === 1 ? stripped : char.toLowerCase()
    }

    return folded
  }

  move(step) {
    if (!this.hits.length) return

    this.current = (this.current + step + this.hits.length) % this.hits.length
    this.render()
    this.reveal()
  }

  reveal() {
    this.hits.forEach(mark => mark.classList.remove('ap-hit--current'))
    if (this.current < 0) return

    const mark = this.hits[this.current]
    mark.classList.add('ap-hit--current')
    mark.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  render() {
    const total = this.hits.length
    const perSection = this.countBySection()

    this.controlsTarget.hidden  = false
    this.countTarget.textContent = total ? `${this.current + 1} / ${total}` : '0'
    this.countTarget.classList.toggle('ap-search__count--empty', total === 0)
    this.inputTarget.style.paddingRight = `${this.controlsTarget.offsetWidth + 16}px`

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = total
        ? I18n.t('preferences.search.summary', { count: total, sections: perSection.size })
        : I18n.t('preferences.search.none')
    }

    this.tocLinkTargets.forEach((link, index) => {
      const section = (link.getAttribute('href') || '').slice(1)
      const count   = perSection.get(section) || 0

      this.badgeFor(link).textContent = count || ''
      this.tocRowTargets[index]?.classList.toggle('ap-search__row--empty', count === 0)
    })
  }

  reset() {
    this.controlsTarget.hidden = true
    this.inputTarget.style.paddingRight = ''
    if (this.hasSummaryTarget) this.summaryTarget.textContent = this.heading

    this.tocLinkTargets.forEach((link, index) => {
      this.badgeFor(link).textContent = ''
      this.tocRowTargets[index]?.classList.remove('ap-search__row--empty')
    })
  }

  badgeFor(link) {
    let badge = link.nextElementSibling

    if (!badge || !badge.classList.contains('ap-search__badge')) {
      badge = document.createElement('span')
      badge.className = 'ap-search__badge'
      link.after(badge)
    }

    return badge
  }

  // The sections are flat siblings inside the fieldset, told apart only by the
  // bare <a name="toc-…"> anchor that precedes each one.
  countBySection() {
    const counts   = new Map()
    const fieldset = this.element.querySelector('fieldset')
    if (!fieldset) return counts

    const sections = new Map()
    let   section  = null

    Array.from(fieldset.children).forEach(child => {
      if (child.tagName === 'A' && (child.getAttribute('name') || '').startsWith('toc-')) {
        section = child.getAttribute('name')
      }
      sections.set(child, section)
    })

    this.hits.forEach(mark => {
      let node = mark
      while (node && node.parentElement !== fieldset) node = node.parentElement
      if (!node) return

      const name = sections.get(node)
      if (name) counts.set(name, (counts.get(name) || 0) + 1)
    })

    return counts
  }

  shortcutLabel() {
    return /Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘F' : 'Ctrl F'
  }
}
